import { Typography } from "@/components/atoms";
import { PointsOfInterest, RM_TAGS } from "@/redux/apis/clientPoi/types";
import { FavouriteLocationRowMenu } from "./components/FavouriteLocationRowMenu";
import { FavouriteLocationsSortKey, FavouriteLocationsTableData } from "./types";

export interface SplitFavourites {
  home?: PointsOfInterest;
  work?: PointsOfInterest;
  /** Everything that isn't Home/Work — the sortable, paginated list (matches mobile's "Other" list). */
  others: PointsOfInterest[];
}

/**
 * Mobile parity: Home and Work are pulled out of the list and pinned, everything else stays in the
 * sortable/paginated table. See app-customer FavouriteLocationsScreen, which renders Home/Work as fixed
 * rows above a list of `location_other` favourites.
 */
export const splitFavourites = (locations: PointsOfInterest[]): SplitFavourites => {
  const home = locations.find((location) => location.rm_tags?.includes(RM_TAGS.Home));
  const work = locations.find((location) => location.rm_tags?.includes(RM_TAGS.Work));
  const others = locations.filter((location) => location.uuid !== home?.uuid && location.uuid !== work?.uuid);
  return { home, work, others };
};

/** Client-side sort for the "Other" favourites — we fetch all rows at once, so the server no longer sorts. */
export const sortFavourites = (
  locations: PointsOfInterest[],
  key: FavouriteLocationsSortKey | undefined,
  desc: boolean
): PointsOfInterest[] => {
  if (!key) return locations;
  return [...locations].sort((a, b) => {
    const cmp = (a[key] ?? "").toString().localeCompare((b[key] ?? "").toString(), undefined, { sensitivity: "base" });
    return desc ? -cmp : cmp;
  });
};

export type LocationCategory = "home" | "work" | "custom";

const CATEGORY_LABELS: Record<LocationCategory, string> = { home: "Home", work: "Work", custom: "Custom" };

/** Derives the favourite's category (Home/Work/Custom) from its backend rm_tags. */
export const getLocationCategory = (location: Pick<PointsOfInterest, "rm_tags">): LocationCategory =>
  location.rm_tags?.includes(RM_TAGS.Home) ? "home" : location.rm_tags?.includes(RM_TAGS.Work) ? "work" : "custom";

export const getLocationCategoryLabel = (location: Pick<PointsOfInterest, "rm_tags">): string =>
  CATEGORY_LABELS[getLocationCategory(location)];

/** Maps a selected category back to the backend rm_tags value (mobile parity). */
export const categoryToRmTags = (category: LocationCategory): string[] =>
  category === "home" ? [RM_TAGS.Home] : category === "work" ? [RM_TAGS.Work] : [RM_TAGS.Other];

export const generateFavouriteLocationsData = (locations: PointsOfInterest[], accountId?: string): FavouriteLocationsTableData[] =>
  locations.map((location) => ({
    locationName: <Typography>{location.rm_label}</Typography>,
    category: <Typography>{getLocationCategoryLabel(location)}</Typography>,
    address: (
      <Typography className="block max-w-[700px] truncate" title={location.address}>
        {location.address}
      </Typography>
    ),
    action: <FavouriteLocationRowMenu location={location} accountId={accountId} />,
  }));
